import { Directive, ElementRef, Renderer2, OnDestroy, Input, Output, EventEmitter } from '@angular/core';

/**
 * Custom ModalDirective to replace ngx-bootstrap ModalDirective
 * Provides show() and hide() methods compatible with Bootstrap modal classes
 */
@Directive({
    selector: '[bsModal]',
    exportAs: 'bs-modal',
    standalone: true
})
export class ModalDirective implements OnDestroy {
    @Input() config: any = {};
    @Output() onShow: EventEmitter<any> = new EventEmitter();
    @Output() onShown: EventEmitter<any> = new EventEmitter();
    @Output() onHide: EventEmitter<any> = new EventEmitter();
    @Output() onHidden: EventEmitter<any> = new EventEmitter();

    private isShown = false;
    private backdrop: HTMLElement | null = null;
    private clickListener: (() => void) | null = null;
    private keydownListener: ((e: KeyboardEvent) => void) | null = null;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {}

    /**
     * Shows the modal
     */
    show(): void {
        if (this.isShown) return;

        this.onShow.emit();
        this.isShown = true;

        // Create backdrop
        this.createBackdrop();

        // Add classes to modal
        const modalElement = this.el.nativeElement;
        this.renderer.addClass(modalElement, 'show');
        this.renderer.setStyle(modalElement, 'display', 'block');
        this.renderer.setAttribute(modalElement, 'aria-modal', 'true');
        this.renderer.removeAttribute(modalElement, 'aria-hidden');

        // Add class to body to prevent scrolling
        this.renderer.addClass(document.body, 'modal-open');

        // Add escape key listener
        this.keydownListener = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.isShown) {
                this.hide();
            }
        };
        document.addEventListener('keydown', this.keydownListener);

        // Emit shown event after a short delay for animation
        setTimeout(() => {
            this.onShown.emit();
        }, 150);
    }

    /**
     * Hides the modal
     */
    hide(): void {
        if (!this.isShown) return;

        this.onHide.emit();
        this.isShown = false;

        // Remove classes from modal
        const modalElement = this.el.nativeElement;
        this.renderer.removeClass(modalElement, 'show');
        this.renderer.setStyle(modalElement, 'display', 'none');
        this.renderer.removeAttribute(modalElement, 'aria-modal');
        this.renderer.setAttribute(modalElement, 'aria-hidden', 'true');

        // Remove backdrop
        this.removeBackdrop();

        // Remove class from body
        this.renderer.removeClass(document.body, 'modal-open');

        // Remove escape key listener
        if (this.keydownListener) {
            document.removeEventListener('keydown', this.keydownListener);
            this.keydownListener = null;
        }

        // Emit hidden event after a short delay for animation
        setTimeout(() => {
            this.onHidden.emit();
        }, 150);
    }

    /**
     * Toggles the modal visibility
     */
    toggle(): void {
        if (this.isShown) {
            this.hide();
        } else {
            this.show();
        }
    }

    private createBackdrop(): void {
        this.backdrop = this.renderer.createElement('div');
        this.renderer.addClass(this.backdrop, 'modal-backdrop');
        this.renderer.addClass(this.backdrop, 'fade');
        this.renderer.addClass(this.backdrop, 'show');
        this.renderer.appendChild(document.body, this.backdrop);

        // Close on backdrop click (if not configured to ignore)
        if (!this.config?.ignoreBackdropClick) {
            this.clickListener = () => this.hide();
            this.backdrop.addEventListener('click', this.clickListener);
        }
    }

    private removeBackdrop(): void {
        if (this.backdrop) {
            if (this.clickListener) {
                this.backdrop.removeEventListener('click', this.clickListener);
                this.clickListener = null;
            }
            this.renderer.removeChild(document.body, this.backdrop);
            this.backdrop = null;
        }
    }

    ngOnDestroy(): void {
        if (this.isShown) {
            this.hide();
        }
    }
}
